import { useQuery } from '@apollo/client';
import { IconButton, Stack, Text, TooltipHost } from '@fluentui/react';
import { IColumn } from '@fluentui/react/lib/DetailsList';
import { GenericAttachForm } from 'common/components/ActionMenu/Attach/AttachFormModal';
import { DocumentDataCallout } from 'common/components/AttachDocumentModal/DocumentDataCallout';
import { DocumentViewModalState } from 'common/components/DocumentList';
import { TABLE_ROWS } from 'common/constants';
import {
  dateConvertions,
  dateFormat,
  getGlobalDateFormat,
} from 'common/utils/dateFormats';
import React, { useCallback } from 'react';
import { UserSetupCommonData } from 'settings/account/userSetup/__generated__/UserSetupCommonData';
import {
  AttachableUserProfileDocuments,
  AttachableUserProfileDocumentsVariables,
  AttachableUserProfileDocuments_attachableUserProfileDocuments_nodes,
} from '../../__generated__/AttachableUserProfileDocuments';
import { UserProfile_userProfile } from '../../__generated__/UserProfile';
import { _columns } from './columns.data';
import { useStyles } from './index.styles';
import { toOrderByVariable } from './utils';

import ATTACHABLE_USER_DOCUMENTS from '../../AttachableUserProfileDocuments.graphql';
import USER_AVAILABLE_DOCUMENT_TYPES from '../../../UserSetupCommonData.graphql';

export type AttachableDataType =
  AttachableUserProfileDocuments_attachableUserProfileDocuments_nodes;

export interface AttachedDocumentsDataType {
  documentTypeId: number | null | undefined;
  entityDocumentId: string[] | null;
  attachedDocuments: AttachableDataType[];
}

interface UserDocumentsProps {
  userProfileDetails?: UserProfile_userProfile | null | undefined;
  onDocumentsAttached: (
    attachedDocumentsData: AttachedDocumentsDataType
  ) => void;
  prevSelectedDocuments?: AttachableDataType[];
}

export const UserDocuments: React.FC<UserDocumentsProps> = ({
  userProfileDetails,
  onDocumentsAttached,
  prevSelectedDocuments,
}) => {
  const styles = useStyles();

  const { data: AvailableDocumentTypes } = useQuery<UserSetupCommonData>(
    USER_AVAILABLE_DOCUMENT_TYPES,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-only',
    }
  );

  const getDocumentTypeOptions = useCallback(
    (data: UserSetupCommonData | undefined) => {
      return (
        data?.userProfileAvailableDocumentTypes?.nodes.map((doctype) => ({
          key: doctype.id,
          text: doctype.documentType || '',
        })) || []
      );
    },
    []
  );

  const transformData = useCallback(
    (data: AttachableUserProfileDocuments | undefined) => {
      const documents = data?.attachableUserProfileDocuments?.nodes.map(
        (document) =>
          ({
            ...document,
            available: document.documentAppliedAmounts?.remainingTotal,
            isoCode: document.currency?.isoCode,
          } as AttachableDataType)
      );
      if (prevSelectedDocuments?.length && documents?.length) {
        return documents.filter(
          ({ id: id1 }) =>
            !prevSelectedDocuments.some(({ id: id2 }) => id2 === id1)
        );
      }
      return documents;
    },
    [prevSelectedDocuments]
  );

  const onRenderItemColumn = useCallback(
    (
      item?: AttachableDataType,
      _index?: number,
      column?: IColumn,
      openDocumentViewModal?: (state: DocumentViewModalState) => void
    ) => {
      if (!item || !column) return undefined;

      const fieldContent = item[
        column.fieldName as keyof AttachableDataType
      ] as string | null;

      switch (column.key) {
        case 'fileReference':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <DocumentDataCallout item={item} />
            </Stack>
          );
        case 'documentType':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text>{item.documentTypes?.documentType}</Text>
            </Stack>
          );
        case 'isoCode':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text>{item.currency?.isoCode}</Text>
            </Stack>
          );
        case 'view':
          const viewDocumentVisible =
            item._isProtected! || item._fileViewer !== 'browser';
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <TooltipHost content="View" id="tooltipId">
                <IconButton
                  disabled={viewDocumentVisible}
                  iconProps={{ iconName: 'View' }}
                  onClick={() =>
                    openDocumentViewModal?.({
                      isOpen: true,
                      title: item.fileReference,
                      entityDocumentId: item.id,
                      _fileType: item._fileType!,
                    })
                  }
                />
              </TooltipHost>
            </Stack>
          );
        case '_uploadDate':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text>{getGlobalDateFormat(item._uploadDate!)}</Text>
            </Stack>
          );
        case 'indexTransactionDate':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              {item.indexTransactionDate && (
                <Text>
                  {dateFormat(dateConvertions(item.indexTransactionDate))}
                </Text>
              )}
            </Stack>
          );
        default:
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text>{fieldContent}</Text>
            </Stack>
          );
      }
    },
    [styles]
  );

  const getMutationInput = useCallback(
    (
      selectedItems: AttachableDataType[],
      entityId: string,
      documentTypeId?: number | null
    ) => {
      const documentsIDs: string[] = selectedItems.map((ele) => ele.id);
      return {
        documentTypeId,
        entityDocumentId: documentsIDs,
        attachedDocuments: selectedItems,
      } as AttachedDocumentsDataType;
    },
    []
  );

  const onAttachedSuccessfully = useCallback(() => {
    onDocumentsAttached({
      documentTypeId: undefined,
      entityDocumentId: [],
      attachedDocuments: [],
    });
  }, [onDocumentsAttached]);

  return (
    <GenericAttachForm<
      AttachableDataType,
      AttachableUserProfileDocuments,
      AttachableUserProfileDocumentsVariables,
      unknown,
      AttachedDocumentsDataType,
      UserSetupCommonData
    >
      entity={{ id: userProfileDetails?.id }}
      availableDocumentTypes={AvailableDocumentTypes}
      attachableDocumentsQuery={ATTACHABLE_USER_DOCUMENTS}
      detailsQuery={ATTACHABLE_USER_DOCUMENTS} // Dummy, as not used
      columns={_columns}
      getDocumentTypeOptions={getDocumentTypeOptions}
      transformData={transformData}
      getMutationInput={getMutationInput}
      onRenderItemColumn={onRenderItemColumn}
      initialQueryVariables={{
        userProfileId: userProfileDetails?.id,
        first: TABLE_ROWS,
      }}
      toOrderByVariable={toOrderByVariable}
      onAttachedSuccessfully={onAttachedSuccessfully}
      buttonRightAligned
    />
  );
};
